"use client";

import type React from "react";

import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import type {
  ExpensesOutput,
  ExpensesQueryParams,
} from "@/interfaces/expenses";
import { ExpensesService } from "@/services/ExpenseService";
import {
  Trash,
  Plus,
  Filter,
  X,
  Edit,
  Receipt,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import type { TagsOutput, PaginatedResponse, Pagination } from "@/interfaces";
import { Badge } from "@/components/ui/badge";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TagsService } from "@/services/TagService";
import CustomCombobox from "@/components/CustomCombobox";
import { DatePickerComponent } from "@/components/DatePickerComponent";
import { DeleteConfirmationAlert } from "@/components/DeleteConfirmationAlert";
import { useDeleteConfirmation } from "@/hooks/use-delete-confirmation";
import { numberToMoney } from "@/util/moneyConversion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const FilterContent = ({
  queryParams,
  setQueryParams,
  availableTags,
  onClose,
  onApplyFilters,
}: {
  queryParams: ExpensesQueryParams;
  setQueryParams: React.Dispatch<React.SetStateAction<ExpensesQueryParams>>;
  availableTags: TagsOutput[];
  onClose?: () => void;
  onApplyFilters: () => void;
}) => {
  const [tempQueryParams, setTempQueryParams] =
    useState<ExpensesQueryParams>(queryParams);

  const handleApplyFilters = () => {
    setQueryParams(tempQueryParams);
    onApplyFilters();
    onClose?.();
  };

  const tagOptions = availableTags.map((tag) => ({
    value: tag.id,
    label: tag.name,
  }));

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-medium mb-2">Nome da despesa</h4>
        <div className="flex space-x-2">
          <Input
            placeholder="Digite o nome"
            className="h-8"
            value={tempQueryParams.query || ""}
            onChange={(e) =>
              setTempQueryParams((prev) => ({ ...prev, query: e.target.value }))
            }
          />
        </div>
      </div>

      <div>
        <h4 className="font-medium mb-2">Tags</h4>
        <CustomCombobox
          value={tempQueryParams.tags?.[0] || ""}
          onChange={(value) => {
            setTempQueryParams((prev) => ({
              ...prev,
              tags: [value],
            }));
          }}
          options={tagOptions}
          placeholder="Selecione uma tag"
          className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
        />
      </div>

      <div>
        <h4 className="font-medium mb-2">Período</h4>
        <div className="flex space-x-2">
          <DatePickerComponent
            selected={
              tempQueryParams.startDate
                ? new Date(tempQueryParams.startDate)
                : undefined
            }
            onSelect={(date) =>
              setTempQueryParams((prev) => ({
                ...prev,
                startDate: date ? format(date, "yyyy-MM-dd") : undefined,
              }))
            }
            placeholder="Data inicial"
            className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
          />
          <DatePickerComponent
            selected={
              tempQueryParams.endDate
                ? new Date(tempQueryParams.endDate)
                : undefined
            }
            onSelect={(date) =>
              setTempQueryParams((prev) => ({
                ...prev,
                endDate: date ? format(date, "yyyy-MM-dd") : undefined,
              }))
            }
            placeholder="Data final"
            className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
          />
        </div>
      </div>

      <Button
        onClick={handleApplyFilters}
        className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
      >
        Aplicar Filtros
      </Button>
    </div>
  );
};

const PaginationControls = ({
  pagination,
  onPageChange,
  onPageSizeChange,
}: {
  pagination: Pagination;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}) => {
  const { page, pageSize, total, pages } = pagination;
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-2">
      <div className="text-sm text-gray-500">
        Mostrando {total > 0 ? startItem : 0} a {endItem} de {total} despesas
      </div>

      <div className="flex items-center gap-2">
        <div className="flex items-center mr-4">
          <span className="text-sm text-gray-500 mr-2">Itens por página:</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={pageSize.toString()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-1">
            {/* Mostrar pelo menos a página 1 mesmo quando há apenas uma página */}
            {pages === 0 ? (
              <Button
                variant="default"
                size="icon"
                className="h-8 w-8 bg-[#EE7B11] hover:bg-[#d06a0a]"
              >
                1
              </Button>
            ) : (
              Array.from(
                { length: Math.max(1, Math.min(pages, 5)) },
                (_, i) => {
                  let pageNumber: number;

                  // Logic to show pages around current page
                  if (pages <= 5) {
                    pageNumber = i + 1;
                  } else if (page <= 3) {
                    pageNumber = i + 1;
                  } else if (page >= pages - 2) {
                    pageNumber = pages - 4 + i;
                  } else {
                    pageNumber = page - 2 + i;
                  }

                  return (
                    <Button
                      key={pageNumber}
                      variant={page === pageNumber ? "default" : "outline"}
                      size="icon"
                      className={cn(
                        "h-8 w-8",
                        page === pageNumber && "bg-[#EE7B11] hover:bg-[#d06a0a]"
                      )}
                      onClick={() => onPageChange(pageNumber)}
                    >
                      {pageNumber}
                    </Button>
                  );
                }
              )
            )}
          </div>

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const ExpenseCard = ({
  expense,
  onEdit,
  onDelete,
}: {
  expense: ExpensesOutput;
  onEdit: () => void;
  onDelete: () => void;
}) => (
  <div className="p-4 border-b last:border-b-0 space-y-2">
    <div className="flex justify-between items-start">
      <div>
        <h3 className="font-medium">{expense.name}</h3>
        <p className="text-sm text-gray-500">
          {numberToMoney(expense.totalPaid)}
        </p>
        <p className="text-sm text-gray-500">
          {format(new Date(expense.expenseDate), "dd/MM/yyyy")}
        </p>
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
          onClick={onEdit}
        >
          <Edit className="h-4 w-4 mr-1" />
          Editar
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="border-red-500 text-red-500 hover:bg-red-50"
          onClick={onDelete}
        >
          <Trash className="h-4 w-4" />
        </Button>
      </div>
    </div>
    {(expense.tags as TagsOutput[]).length > 0 && (
      <div className="flex flex-wrap gap-1">
        {(expense.tags as TagsOutput[]).map((tag) => (
          <Badge
            key={tag?.id}
            style={{
              backgroundColor: tag?.styling?.primaryColor ?? "#E0E0E0",
              color: tag?.styling?.fontColor ?? "#000000",
              border: `1px solid ${tag?.styling?.secondaryColor ?? "#BDBDBD"}`,
            }}
            className="rounded-sm px-1 py-0.5 text-xs"
          >
            {tag?.name}
          </Badge>
        ))}
      </div>
    )}
  </div>
);

const Expenses = () => {
  const [expenses, setExpenses] = useState<ExpensesOutput[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [queryParams, setQueryParams] = useState<ExpensesQueryParams>({
    expand: ["tags"],
    startDate: format(startOfMonth(new Date()), "yyyy-MM-dd"),
    endDate: format(endOfMonth(new Date()), "yyyy-MM-dd"),
  });
  const [tags, setTags] = useState<TagsOutput[]>([]);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const navigate = useNavigate();
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: 10,
    total: 0,
    pages: 0,
    links: {
      self: "",
    },
  });

  // Referência para controlar chamadas duplicadas
  const isFetchingRef = useRef(false);

  const expenseService = new ExpensesService();
  const tagsService = new TagsService();

  // Usando o hook para gerenciar o estado de exclusão
  const {
    isOpen,
    itemToDelete,
    open: openDeleteDialog,
    close: closeDeleteDialog,
  } = useDeleteConfirmation<string>();

  const fetchExpenses = async () => {
    // Evitar chamadas duplicadas
    if (isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      setIsLoading(true);

      const params = {
        ...queryParams,
        page: pagination.page,
        pageSize: pagination.pageSize,
      };

      const response: PaginatedResponse<ExpensesOutput> =
        await expenseService.findAll(params);

      setExpenses(response.data);

      // Verificar se a página atual é válida
      if (
        response.pagination.pages > 0 &&
        pagination.page > response.pagination.pages
      ) {
        setPagination((prev) => ({
          ...prev,
          page: response.pagination.pages,
        }));
      } else {
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error("Erro ao buscar despesas:", error);
      // Se ocorrer um erro, voltar para a página 1
      if (pagination.page !== 1) {
        setPagination((prev) => ({
          ...prev,
          page: 1,
        }));
      }
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  const fetchTags = async () => {
    const { data: fetchedTags } = await tagsService.findAll();
    setTags(fetchedTags);
  };

  // Efeito para carregar dados iniciais
  useEffect(() => {
    fetchTags();
    fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Efeito para atualizar quando os filtros ou paginação mudarem
  useEffect(() => {
    fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, pagination.page, pagination.pageSize]);

  const handleNavigateToForm = (expenseId?: string) => {
    const targetRoute = expenseId
      ? `/despesas/${expenseId}/editar`
      : "/despesas/novo";
    navigate(targetRoute);
  };

  const handleDelete = (id: string) => {
    openDeleteDialog(id);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      try {
        setIsLoading(true);
        await expenseService.delete(itemToDelete);
        await fetchExpenses();
      } finally {
        setIsLoading(false);
      }
    }
    closeDeleteDialog();
  };

  const removeFilter = (filterType: keyof ExpensesQueryParams) => {
    setQueryParams((prev) => {
      const newParams = { ...prev };
      delete newParams[filterType];
      return newParams;
    });

    // Resetar para a primeira página ao remover filtros
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: newPageSize,
      page: 1, // Reset to first page when changing page size
    }));
  };

  if (isLoading && expenses.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  const FilterButton = () => (
    <>
      {/* Desktop: Popover */}
      <div className="hidden md:block">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="h-8 border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
            >
              <Filter className="mr-2 h-4 w-4" />
              Filtrar
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <FilterContent
              queryParams={queryParams}
              setQueryParams={setQueryParams}
              availableTags={tags}
              onApplyFilters={fetchExpenses}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Mobile: Sheet */}
      <div className="md:hidden">
        <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              className="h-8 border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
            >
              <Filter className="mr-2 h-4 w-4" />
              Filtrar
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[80vh]">
            <SheetHeader>
              <SheetTitle>Filtros</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <FilterContent
                queryParams={queryParams}
                setQueryParams={setQueryParams}
                availableTags={tags}
                onClose={() => setIsFilterSheetOpen(false)}
                onApplyFilters={fetchExpenses}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );

  return (
    <div className="p-4 sm:p-6 w-full max-w-none">
      <Card className="overflow-hidden border-none shadow-md w-full">
        <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white">
          <div className="flex items-center gap-3">
            <Receipt className="h-6 w-6" />
            <CardTitle className="text-lg">Gerenciamento de Despesas</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <FilterButton />
              <div className="flex flex-wrap gap-2">
                {queryParams.query && (
                  <Badge variant="secondary" className="h-8">
                    Nome: {queryParams.query}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2 h-4 w-4 p-0"
                      onClick={() => removeFilter("query")}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                )}
                {queryParams.startDate && queryParams.endDate && (
                  <Badge variant="secondary" className="h-8">
                    Período:{" "}
                    {format(new Date(queryParams.startDate), "dd/MM/yyyy")} -{" "}
                    {format(new Date(queryParams.endDate), "dd/MM/yyyy")}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2 h-4 w-4 p-0"
                      onClick={() => {
                        removeFilter("startDate");
                        removeFilter("endDate");
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                )}
                {queryParams.tags && queryParams.tags.length > 0 && (
                  <Badge variant="secondary" className="h-8">
                    Tag:{" "}
                    {tags.find((tag) => tag.id === queryParams.tags?.[0])
                      ?.name || queryParams.tags?.[0]}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2 h-4 w-4 p-0"
                      onClick={() => removeFilter("tags")}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                )}
              </div>
            </div>

            <Button
              onClick={() => handleNavigateToForm()}
              size="sm"
              className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white w-full md:w-auto"
            >
              <Plus className="h-4 w-4 mr-2" /> Cadastrar Despesa
            </Button>
          </div>

          {expenses.length > 0 ? (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader className="bg-gray-50">
                    <TableRow>
                      <TableHead className="text-sm">Nome da despesa</TableHead>
                      <TableHead className="text-sm">Total pago</TableHead>
                      <TableHead className="text-sm">Tags</TableHead>
                      <TableHead className="text-sm">Data da despesa</TableHead>
                      <TableHead className="text-right text-sm">
                        Ações
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenses.map((expense) => (
                      <TableRow key={expense.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">
                          {expense.name}
                        </TableCell>
                        <TableCell>
                          {numberToMoney(expense.totalPaid)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(expense.tags as TagsOutput[]).map((tag) => (
                              <Badge
                                key={tag?.id}
                                style={{
                                  backgroundColor:
                                    tag?.styling?.primaryColor ?? "#E0E0E0",
                                  color: tag?.styling?.fontColor ?? "#000000",
                                  border: `1px solid ${
                                    tag?.styling?.secondaryColor ?? "#BDBDBD"
                                  }`,
                                }}
                                className="rounded-sm px-1 py-0.5 text-xs"
                              >
                                {tag?.name}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          {format(new Date(expense.expenseDate), "dd/MM/yyyy")}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
                              onClick={() => handleNavigateToForm(expense.id)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Editar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-500 text-red-500 hover:bg-red-50"
                              onClick={() => handleDelete(expense.id)}
                            >
                              <Trash className="h-4 w-4 mr-1" />
                              Excluir
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card List */}
              <div className="md:hidden divide-y border rounded-md">
                {expenses.map((expense) => (
                  <ExpenseCard
                    key={expense.id}
                    expense={expense}
                    onEdit={() => handleNavigateToForm(expense.id)}
                    onDelete={() => handleDelete(expense.id)}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12 border rounded-lg bg-gray-50 w-full">
              <Receipt className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <h3 className="text-base font-medium text-gray-700 mb-2">
                Nenhuma despesa encontrada
              </h3>
              <p className="text-gray-500 mb-6">
                {Object.keys(queryParams).some((key) => key !== "expand")
                  ? "Não encontramos despesas com os filtros aplicados"
                  : "Você ainda não possui despesas cadastradas"}
              </p>
              <Button
                onClick={() => handleNavigateToForm()}
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Cadastrar Despesa
              </Button>
            </div>
          )}

          {expenses.length > 0 && (
            <PaginationControls
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>

      {/* Componente de confirmação de exclusão */}
      <DeleteConfirmationAlert
        isOpen={isOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir esta despesa? Esta ação não pode ser desfeita."
        itemName={
          expenses.find((e) => e.id === itemToDelete)?.name || "esta despesa"
        }
      />
    </div>
  );
};

export default Expenses;
